import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Map, Play, Truck, Users, Link as LinkIcon,
  Wrench, Receipt, DollarSign, FileText,
  KeyRound, ListChecks, FileArchive, Undo2, BarChart3, Tag, Cpu, Settings, Building2,
  CalendarClock, ClipboardList, MessageCircle, Share2,
  ChevronRight, ChevronDown,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/store/ui-store';
import { useAuthStore } from '@/store/auth-store';
import { useT } from '@/lib/i18n';
import { api } from '@/lib/api';
import { appBrand } from '@/lib/branding';

// =============================================================================
// Sidebar dengan collapsible group. Group muncul kalau levels-nya cocok
// dengan user level_id (admin/reseller/dst). Klik header group toggle expand
// di-persist via ui-store (Set string keys yang expanded).
// =============================================================================

interface NavItem {
  label: string;
  href: string;
  icon: typeof Map;
  levels?: number[];
}

interface NavGroup {
  label: string;
  icon: typeof Map;
  children: NavItem[];
  levels?: number[];
}

type NavEntry =
  | { kind: 'item';  item: NavItem }
  | { kind: 'group'; group: NavGroup };

// NAV labels disimpan sebagai i18n KEY (mis. 'sidebar.monitoring'); resolved
// via useT() saat render. Kalau key tidak ada di translation, fallback ke
// literal label di i18n.ts (lihat useT).
const NAV: NavEntry[] = [
  { kind: 'item', item: { label: 'sidebar.dashboard',    href: '/dashboard',     icon: BarChart3 } },
  { kind: 'item', item: { label: 'sidebar.monitoring',   href: '/monitoring',    icon: Map } },
  { kind: 'group', group: {
    label: 'sidebar.report', icon: BarChart3,
    children: [
      { label: 'sidebar.report.playback',          href: '/reports/playback',           icon: Play },
      { label: 'sidebar.report.temperature',       href: '/reports/temperature',        icon: BarChart3 },
      { label: 'sidebar.report.stopPark',          href: '/reports/stop-parkir',        icon: BarChart3 },
      { label: 'sidebar.report.events',            href: '/reports/events',             icon: FileText },
      { label: 'sidebar.report.unileverShipment',  href: '/reports/unilever-shipment',  icon: FileText },
    ],
  } },
  { kind: 'item', item: { label: 'sidebar.vehicles',     href: '/vehicles',      icon: Truck } },
  { kind: 'item', item: { label: 'sidebar.users',        href: '/users',         icon: Users } },
  { kind: 'item', item: { label: 'sidebar.deviceMember', href: '/device-member', icon: LinkIcon, levels: [1, 4] } },
  { kind: 'group', group: {
    label: 'sidebar.maintenance', icon: Wrench,
    children: [
      { label: 'sidebar.maintenance.schedule', href: '/maintenance/schedule', icon: CalendarClock },
      { label: 'sidebar.maintenance.records',  href: '/maintenance/records',  icon: ClipboardList },
    ],
  } },

  { kind: 'group', group: {
    label: 'sidebar.rental', icon: Receipt,
    children: [
      { label: 'sidebar.rental.input',     href: '/rental',           icon: Receipt },
      { label: 'sidebar.rental.return',    href: '/rental/return',    icon: Undo2 },
      { label: 'sidebar.rental.report',    href: '/rental/report',    icon: BarChart3 },
      { label: 'sidebar.rental.checklist', href: '/rental/checklist', icon: ListChecks },
    ],
  } },

  { kind: 'group', group: {
    label: 'sidebar.invoice', icon: FileText, levels: [1],
    children: [
      { label: 'sidebar.invoice.cost',     href: '/vehicle-cost', icon: DollarSign },
      { label: 'sidebar.invoice.generate', href: '/invoice',      icon: FileArchive },
    ],
  } },

  { kind: 'group', group: {
    label: 'sidebar.integration', icon: Settings, levels: [1, 4],
    children: [
      { label: 'sidebar.integration.unileverSetting',  href: '/integration/unilever-setting',  icon: KeyRound },
      { label: 'sidebar.integration.unileverShipment', href: '/integration/unilever-shipment', icon: FileText },
      { label: 'sidebar.integration.stanley',          href: '/integration/stanley',           icon: Share2 },
    ],
  } },

  { kind: 'item', item: { label: 'sidebar.apiKeys',         href: '/api-keys',         icon: KeyRound,   levels: [1] } },
  { kind: 'item', item: { label: 'sidebar.vehicleBrand',    href: '/vehicle-brands',   icon: Tag,        levels: [1] } },
  { kind: 'item', item: { label: 'sidebar.gpsBrand',        href: '/gps-brands',       icon: Cpu,        levels: [1] } },
  { kind: 'item', item: { label: 'sidebar.platformProfile', href: '/platform-profile', icon: Building2, levels: [1] } },
  { kind: 'item', item: { label: 'sidebar.whatsappSetup', href: '/whatsapp-setup', icon: MessageCircle, levels: [1] } },
  //{ kind: 'item', item: { label: 'sidebar.tenants',         href: '/tenants',         icon: Building2,   levels: [1] } },
  { kind: 'item', item: { label: 'sidebar.activeShares',    href: '/shares',           icon: Share2 } },
];

function levelMatch(itemLevels: number[] | undefined, userLevel: number | undefined): boolean {
  if (!itemLevels || itemLevels.length === 0) return true;
  return userLevel != null && itemLevels.includes(userLevel);
}

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const level = useAuthStore((s) => s.claims?.level_id);
  const location = useLocation();
  const t = useT();

  // Track group expand state â€” auto-open kalau salah satu child aktif.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const init = new Set<string>();
    for (const e of NAV) {
      if (e.kind === 'group') {
        if (e.group.children.some((c) => location.pathname.startsWith(c.href))) {
          init.add(e.group.label);
        }
      }
    }
    return init;
  });

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  // Brand sidebar mengikuti Vite mode/env agar build pasgps/meptrack/crystal
  // konsisten. Platform profile tetap di-query untuk halaman lain dan cache.
  const profileQ = useQuery({
    queryKey: ['platform-profile-public'],
    queryFn: () => api.platformProfilePublic(),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const brandName = appBrand.name || profileQ.data?.data?.company_name || 'webgps';
  const logoSrc = appBrand.logo || profileQ.data?.data?.logo_url;

  return (
    <aside
      data-chrome="sidebar"
      className={cn(
        'border-r border-border flex flex-col transition-[width] duration-200',
        'shadow-sm dark:shadow-none relative z-10',
        collapsed ? 'w-14' : 'w-56',
      )}
    >
      <div className="h-14 flex items-center justify-center gap-2 border-b border-border shrink-0 px-2">
        <img
          src={logoSrc}
          alt={brandName}
          className="h-8 w-8 shrink-0 object-contain rounded"
        />
        {!collapsed && (
          <span className="text-base font-bold tracking-tight truncate" title={brandName}>
            {brandName}
          </span>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-2 min-h-0">
        {NAV.map((entry, idx) => {
          if (entry.kind === 'item') {
            if (!levelMatch(entry.item.levels, level)) return null;
            return <Item key={entry.item.href} item={entry.item} collapsed={collapsed} t={t} />;
          }
          // Group
          if (!levelMatch(entry.group.levels, level)) return null;
          const visibleChildren = entry.group.children.filter((c) => levelMatch(c.levels, level));
          if (visibleChildren.length === 0) return null;
          const isOpen = openGroups.has(entry.group.label);
          const Icon = entry.group.icon;
          return (
            <div key={`g-${idx}`} className="mb-0.5">
              {/* Group header */}
              {collapsed ? (
                <div
                  className="px-0 flex items-center justify-center py-2 text-muted-foreground"
                  title={entry.group.label}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => toggleGroup(entry.group.label)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:bg-accent/40 hover:text-foreground transition-colors"
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left">{t(entry.group.label)}</span>
                  {isOpen
                    ? <ChevronDown className="w-3.5 h-3.5" />
                    : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
              )}
              {/* Children */}
              {(isOpen || collapsed) && visibleChildren.map((child) => (
                <Item
                  key={child.href}
                  item={child}
                  collapsed={collapsed}
                  indent={!collapsed}
                  t={t}
                />
              ))}
            </div>
          );
        })}
      </nav>

    </aside>
  );
}

function Item({ item, collapsed, indent, t }: {
  item: NavItem; collapsed: boolean; indent?: boolean; t: (k: string) => string;
}) {
  const Icon = item.icon;
  const label = t(item.label);
  return (
    <NavLink
      to={item.href}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors',
          collapsed ? 'justify-center px-0' : indent ? 'px-4 pl-9' : 'px-4',
          isActive && 'bg-accent text-accent-foreground font-medium',
        )
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
}
